/**
 * The root layout: the app shell around every route, with four exceptions.
 *
 * A share link (`/s/:token`, PLAN-LAUNCH D1), a gallery listing (`/g/:entryId`,
 * PLAN-GALLERY G3), a tasting form (`/t/:token`, PLAN-SCORESHEETS S4) and a
 * shared journal (`/j/:token`, PLAN-JOURNAL §F) are all opened by someone who is
 * not signed in and has no workspace, so they render bare. The shell would give
 * them a sidebar full of somebody else's navigation and, worse, would wait on
 * `useHydrated` — the store opening an IndexedDB record that has nothing to do
 * with the page they followed a link to.
 *
 * The check is on the pathname rather than expressed as a pathless layout route
 * because turning every existing route into a child of an `_app` layout is a
 * rename of the whole route tree for four exceptions. All four prefixes are the
 * same constants the link builders use, so they cannot drift.
 */

import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { AppShell } from '@/components/app-shell';
import { RouteNotFound } from '@/components/not-found-page';
import { GALLERY_PAGE_PREFIX } from '@/gallery/protocol';
import { TASTE_PAGE_PREFIX } from '@/scoresheets/protocol';
import { JOURNAL_PAGE_PREFIX, SHARE_PAGE_PREFIX } from '@/share/protocol';

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: RouteNotFound,
});

function RootLayout() {
  const bare = useRouterState({
    select: (state) =>
      state.location.pathname.startsWith(SHARE_PAGE_PREFIX) ||
      state.location.pathname.startsWith(GALLERY_PAGE_PREFIX) ||
      // A tasting link (`/t/:token`, PLAN-SCORESHEETS S4): a stranger with a
      // bottle, who has no workspace to wait on either.
      state.location.pathname.startsWith(TASTE_PAGE_PREFIX) ||
      // A shared journal (`/j/:token`, PLAN-JOURNAL §F): a stranger following a
      // mead, and the same again.
      state.location.pathname.startsWith(JOURNAL_PAGE_PREFIX),
  });

  if (bare) return <Outlet />;

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
